import { Box, Button, Typography, Modal } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { CustomModal, LimitBanner, ReactTable } from '@components';
import { MRT_ToggleGlobalFilterButton } from 'material-react-table';
import { showToast } from '@utils';
import { apiBuyPriceProfile } from '@store';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import useLimitCheck from '@hooks/useLimitCheck';

const YearThree = ({ singlePriceProfile, loading, call, put, volume }) => {
    const dispatch = useDispatch();
    const { uuid } = useParams();
    const [yearThreeTotalState, setYearThreeTotalState] = useState(0);
    const [yearThreeVolState, setYearThreeVolState] = useState(0);
    const [openBuyModal, setOpenBuyModal] = useState(false);
    const [timer, setTimer] = useState(20);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        let tempYearThreeTotal = 0;
        let tempYearThreeVol = 0;

        for (let i in singlePriceProfile) {
            tempYearThreeVol += singlePriceProfile[i].Total;
            tempYearThreeTotal += singlePriceProfile[i].PriceTotal;
        }

        setYearThreeTotalState(tempYearThreeTotal);
        setYearThreeVolState(tempYearThreeVol);
    }, [singlePriceProfile]);

    useEffect(() => {
        if (!openBuyModal) {
            return;
        }

        setTimer(20);
        const interval = setInterval(() => {
            setTimer((currentTimer) => currentTimer - 1);
        }, 1000);

        return () => clearInterval(interval);
    }, [openBuyModal]);

    useEffect(() => {
        if (timer === 0) {
            setOpenBuyModal(false);
        }
    }, [timer]);

    const limitCheck = useLimitCheck({
        action: 'buy',
        currency: 'eur',
        notionalValue: yearThreeTotalState,
        volume: yearThreeVolState
    });

    const hasValidVolume =
        Number.isFinite(parseFloat(volume)) && parseFloat(volume) > 0;

    const handleOpenBuyModal = () => {
        setOpenBuyModal(true);
    };

    const handleBuy = async (e) => {
        e.preventDefault();

        if (
            submitting ||
            !hasValidVolume ||
            limitCheck.isDisabled ||
            limitCheck.isLoading
        ) {
            if (!hasValidVolume) {
                showToast('error', 'Volume must be greater than 0');
            }
            return;
        }

        setSubmitting(true);

        try {
            const year = singlePriceProfile ? singlePriceProfile[0].Year : 0;

            if (year === 0) {
                showToast('error', 'Year not found');
                return;
            }

            const data = {
                uuid: uuid,
                volume: parseFloat(volume),
                call: parseFloat(call),
                put: parseFloat(put),
                year: year
            };

            const resp = await dispatch(apiBuyPriceProfile(data));
            if (resp?.meta?.requestStatus === 'fulfilled') {
                setOpenBuyModal(false);
                showToast('success', 'Successfully bought the profile');
            } else if (resp?.meta?.requestStatus === 'rejected') {
                showToast('error', resp.payload);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const columns = useMemo(
        () => [
            {
                header: 'Month',
                id: 'MonthStr',
                accessorFn: ({ MonthStr }) => MonthStr || '',
                Cell: ({ renderedCellValue }) => <>{renderedCellValue}</>,
                size: 50
            },
            {
                header: 'Year',
                id: 'Year',
                accessorFn: ({ Year }) => Year || '',
                Cell: ({ renderedCellValue }) => <>{renderedCellValue}</>,
                size: 50
            },
            {
                header: (
                    <Box className="flex flex-col">
                        <Box>Total Vol</Box>
                        <Box>(MW)</Box>
                    </Box>
                ),
                id: 'TotalStr',
                accessorFn: ({ TotalStr }) =>
                    TotalStr?.replace(/\B(?=(\d{3})+(?!\d))/g, ',') || '',
                Cell: ({ renderedCellValue }) => <>{renderedCellValue}</>,
                size: 50
            },
            {
                header: 'Hours/Month',
                id: 'MonthHours',
                accessorFn: ({ MonthHours }) => MonthHours || '',
                Cell: ({ renderedCellValue }) => <>{renderedCellValue}</>,
                size: 50
            },
            {
                header: 'MWh/h',
                id: 'TotalMWStr',
                accessorFn: ({ TotalMWStr }) =>
                    TotalMWStr?.replace(/\B(?=(\d{3})+(?!\d))/g, ',') || '',
                Cell: ({ renderedCellValue }) => <>{renderedCellValue}</>,
                size: 50
            },
            {
                header: 'Price',
                id: 'AskPriceStr',
                accessorFn: ({ AskPriceStr }) =>
                    AskPriceStr?.replace(/\B(?=(\d{3})+(?!\d))/g, ',') || '',
                Cell: ({ renderedCellValue }) => <>{renderedCellValue}</>,
                size: 50
            },
            {
                header: 'Flex Price',
                id: 'FlexPriceStr',
                accessorFn: ({ FlexPriceStr }) =>
                    FlexPriceStr?.replace(/\B(?=(\d{3})+(?!\d))/g, ',') || '',
                Cell: ({ renderedCellValue }) => <>{renderedCellValue}</>,
                size: 50
            },
            {
                header: (
                    <Box className="flex flex-col">
                        <Box>Min Take</Box>
                        <Box>(MWh/h)</Box>
                    </Box>
                ),
                id: 'MinTakeStr',
                accessorFn: ({ MinTakeStr }) =>
                    MinTakeStr?.replace(/\B(?=(\d{3})+(?!\d))/g, ',') || '',
                Cell: ({ renderedCellValue }) => <>{renderedCellValue}</>,
                size: 50
            },
            {
                header: (
                    <Box className="flex flex-col">
                        <Box>Max Take</Box>
                        <Box>(MWh/h)</Box>
                    </Box>
                ),
                id: 'MaxTakeStr',
                accessorFn: ({ MaxTakeStr }) =>
                    MaxTakeStr?.replace(/\B(?=(\d{3})+(?!\d))/g, ',') || '',
                Cell: ({ renderedCellValue }) => <>{renderedCellValue}</>,
                size: 50
            },
            {
                header: 'Cost [EUR]',
                id: 'PriceTotalStr',
                accessorFn: ({ PriceTotalStr }) =>
                    PriceTotalStr?.replace(/\B(?=(\d{3})+(?!\d))/g, ',') || '',
                Cell: ({ renderedCellValue }) => <>{renderedCellValue}</>,
                size: 50,
                Footer: () => (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1
                        }}
                    >
                        <Box
                            fontSize={18}
                            display={'flex'}
                            alignItems={'center'}
                            gap={1}
                        >
                            Avg Price:
                            <span className="text-green-700">
                                {(yearThreeTotalState / yearThreeVolState)
                                    .toFixed(2)
                                    ?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            </span>
                        </Box>
                        <Button
                            variant="outlined"
                            color="primary"
                            sx={{
                                p: 0,
                                m: 0,
                                backgroundColor: '#27A9FF',
                                border: '1px solid #27A9FF',
                                color: '#fff',
                                fontSize: '13px',
                                '&:hover': {
                                    color: 'white',
                                    backgroundColor: '#27A9FF',
                                    border: '1px solid #27A9FF'
                                }
                            }}
                            onClick={handleOpenBuyModal}
                        >
                            Buy
                        </Button>
                    </Box>
                )
            }
        ],
        [
            yearThreeTotalState,
            yearThreeVolState,
            handleOpenBuyModal,
            hasValidVolume,
            submitting
        ]
    );

    return (
        <>
            <Modal open={openBuyModal} onClose={() => setOpenBuyModal(false)}>
                <CustomModal
                    handleClose={() => setOpenBuyModal(false)}
                    handleSubmit={handleBuy}
                    modalTitle={`Year Three Profile - Buy Window will be closed in ${timer} seconds`}
                    buttonText={'Buy'}
                    timer={timer}
                    disabled={
                        submitting ||
                        !hasValidVolume ||
                        limitCheck.isDisabled ||
                        limitCheck.isLoading
                    }
                >
                    <LimitBanner
                        messages={[
                            ...limitCheck.messages,
                            ...(!hasValidVolume
                                ? ['Volume must be greater than 0']
                                : [])
                        ]}
                    />
                    <Box className="flex flex-col gap-2 mt-2">
                        <Typography sx={{ color: '#000' }}>
                            Year: {singlePriceProfile?.[0]?.Year}
                        </Typography>
                        <Typography sx={{ color: '#000' }}>
                            Volume: {String(volume)}
                        </Typography>
                        <Typography sx={{ color: '#000' }}>
                            Upside Flex: {String(call)}
                        </Typography>
                        <Typography sx={{ color: '#000' }}>
                            Downside Flex: {String(put)}
                        </Typography>
                        <Typography sx={{ color: '#000' }}>
                            Total MWh: {String(yearThreeVolState)}
                        </Typography>
                        <Typography sx={{ color: '#000' }}>
                            Total Amount: {String(yearThreeTotalState)}
                        </Typography>
                    </Box>
                </CustomModal>
            </Modal>
            <ReactTable
                renderToolbarInternalActions={({ table }) => (
                    <div onMouseDown={(e) => e.stopPropagation()}>
                        <MRT_ToggleGlobalFilterButton table={table} />
                    </div>
                )}
                headerText={'Year Three'}
                columns={columns}
                data={singlePriceProfile || []}
                isLoading={loading}
            />
        </>
    );
};

export default YearThree;
